import { useEffect } from "react";

interface RouteMetaInput {
  title: string;
  description?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "video.other";
  noindex?: boolean;
}

/**
 * SEO helper for a React SPA. Sets document.title, description, canonical,
 * Open Graph and Twitter tags per route. For crawlers that execute JS
 * (Google, Bing) this is sufficient; for scrapers that don't (Facebook,
 * Twitter, LinkedIn preview cards), the PHP backend should render the
 * critical `<meta>` tags server-side on the index.html shim for the
 * public URLs it proxies, OR the PHP renderer for shareable routes.
 * See docs/API_CONTRACT.md → "SEO & Social Sharing".
 */
export function useRouteMeta(input: RouteMetaInput): void {
  useEffect(() => {
    const previous: Array<{
      el: HTMLMetaElement | HTMLLinkElement;
      attr: string;
      value: string | null;
    }> = [];
    const origin =
      import.meta.env.VITE_PUBLIC_SITE_URL ||
      (typeof window !== "undefined" ? window.location.origin : "");
    const path =
      input.canonicalPath ?? (typeof window !== "undefined" ? window.location.pathname : "/");
    const absoluteUrl = `${origin}${path}`;

    const prevTitle = document.title;
    document.title = input.title;

    upsertMeta("name", "description", input.description ?? "");
    upsertMeta("name", "robots", input.noindex ? "noindex, nofollow" : "index, follow");
    upsertMeta("property", "og:title", input.title);
    upsertMeta("property", "og:description", input.description ?? "");
    upsertMeta("property", "og:type", input.ogType ?? "website");
    upsertMeta("property", "og:url", absoluteUrl);
    upsertMeta("name", "twitter:card", input.ogImage ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", input.title);
    upsertMeta("name", "twitter:description", input.description ?? "");
    if (input.ogImage) {
      upsertMeta("property", "og:image", input.ogImage);
      upsertMeta("name", "twitter:image", input.ogImage);
    }

    // Canonical (leaf-only rule — every SPA route is a leaf).
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const prevHref = canonical?.href ?? null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = absoluteUrl;

    return () => {
      document.title = prevTitle;
      for (const { el, attr, value } of previous) {
        if (value === null) el.removeAttribute(attr);
        else el.setAttribute(attr, value);
      }
      if (canonical) {
        if (prevHref === null) canonical.remove();
        else canonical.href = prevHref;
      }
    };

    function upsertMeta(attr: "name" | "property", key: string, content: string) {
      let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      const wasMissing = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      previous.push({ el, attr: "content", value: wasMissing ? null : el.getAttribute("content") });
      el.setAttribute("content", content);
    }
  }, [
    input.title,
    input.description,
    input.canonicalPath,
    input.ogImage,
    input.ogType,
    input.noindex,
  ]);
}
